import {
  type Vec3,
  type EntityId,
  EntityType,
  type CompactEntity,
  type SectorDataMsg,
  type SectorChangeMsg,
  type PlayerStateMsg,
  type EquipmentUpdateMsg,
  type SectorCoord,
  type ShipHangarUpdateMsg,
  type SectorClaim,
  type ActiveSectorEvent,
  type SiegeUpdateMsg,
  type WireMission,
  type FriendEntry,
  type FriendRequestEntry,
  type MarketplaceListing,
  type SystemBountyContract,
  xpRequiredForLevel,
  playerStatsForLevel,
  sectorKey,
  DEFAULT_WEAPON_ID,
  DEFAULT_SHIELD_ID,
  DEFAULT_ENGINE_ID,
  getShipClassTraits,
  getRaceTraits,
  lookupEngine,
  ENGINE_BASELINE,
  BASE_MAX_SPEED,
  BASE_THRUST_FORCE,
  SURFACE_GRAVITY,
  SURFACE_HOVER_OFFSET,
  SURFACE_JET_CEILING,
  SURFACE_JET_OVERSHOOT_DRAG,
  SURFACE_JUMP_VELOCITY,
  SURFACE_JUMP_COOLDOWN_MS,
  SURFACE_JUMP_GROUNDED_TOLERANCE,
  SURFACE_FLORA_BOUNCE,
  SURFACE_WALL_BOUNCE,
} from '@viral-space/shared';
import type { ShipClass, RaceId, ResourceTier } from '@viral-space/shared';

export interface ClientTransform {
  position: Vec3;
  rotation: Vec3;
  velocity: Vec3;
}

export interface ClientEntity {
  id: EntityId;
  type: EntityType;
  transform: ClientTransform;
  previousTransform: ClientTransform;
  targetTransform: ClientTransform;
  interpolationAlpha: number;

  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;

  name?: string;
  shipClass?: ShipClass;
  raceId?: RaceId;
  resourceTier?: ResourceTier;
  disposition?: string;
  /**
   * Template key stamped onto NPC entities so the client can tell a
   * merchant apart from a raider without racing the merchant-hail flow.
   * Absent for non-NPC entities and for legacy NPCs without a template.
   */
  npcTemplateKey?: string;
  ownerId?: EntityId;

  extra: Record<string, unknown>;
  lastServerUpdate: number;
}

/**
 * Fires when a non-local entity’s hull HP drops in an {@link EntityUpdate}
 * (same path as the damage numbers / HP bar). Used for reliable hit VFX when
 * delivery of `HitConfirm` is missing or reordered.
 */
export interface EntityDamageEffectEvent {
  entityId: EntityId;
  entityType: EntityType;
  position: Vec3;
  damage: number;
  isKill: boolean;
}

export interface LocalPlayerState {
  entityId: EntityId | null;
  /**
   * Mongo account id (matches server `session.playerId`). Distinct from
   * `entityId` (ship). Used for territory / siege attacker matching.
   */
  accountPlayerId: string | null;
  name: string;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  energy: number;
  maxEnergy: number;
  /**
   * True while the server has boost gated by the post-depletion cooling
   * lockout. The HUD reads this to repaint the energy bar so the pilot
   * sees the cooldown is in effect (otherwise tiny regen values rounding
   * up to "1/100" can make it look like boost is still flickering on).
   */
  boostLocked: boolean;
  damage: number;
  credits: number;
  xp: number;
  xpRequired: number;
  level: number;
  inventory: InventorySlot[];
  sector: Vec3;
  shipClass: ShipClass | null;
  raceId: RaceId | null;
  isDocked: boolean;
  equipment: EquipmentState;
  /**
   * Hulls this pilot currently owns. Mirrors the server's hangar; populated
   * by `ShipHangarUpdate` messages. Always contains `shipClass` once the
   * server has confirmed the join. Used by the station UI's Hangar tab to
   * distinguish owned ships from ships available for purchase.
   */
  ownedShipClasses: ShipClass[];
}

export interface EquipmentState {
  equippedWeaponId: string;
  equippedShieldId: string;
  equippedEngineId: string;
  weaponAmmo: number;
  weaponMaxAmmo: number;
  shieldCharges: number;
  shieldMaxCharges: number;
  ownedWeapons: Array<{ id: string; ammo: number }>;
  ownedShields: Array<{ id: string; charges: number }>;
  ownedEngines: Array<{ id: string }>;
}

export interface InventorySlot {
  itemId: string;
  quantity: number;
  name: string;
}

export type NotificationType =
  | 'damage'
  | 'death'
  | 'resource'
  | 'chat'
  | 'xp'
  | 'levelUp'
  | 'dockConfirm'
  | 'missionList'
  | 'missionProgress'
  | 'missionComplete'
  | 'stationShop'
  | 'respawn'
  | 'activeBuffs'
  | 'npcItemDrop'
  | 'friendRequest'
  | 'friendStatus'
  | 'tradeReceived'
  | 'tradeSent'
  | 'tradeFailed'
  | 'marketplaceResult'
  | 'marketplaceSold'
  | 'bountyBoard';

export interface GameNotification {
  type: NotificationType;
  data: unknown;
  timestamp: number;
}

const INTERPOLATION_SPEED = 10;
const MAX_NOTIFICATIONS = 100;
/**
 * Hard cap on the locally cached completed-mission log. The server now
 * owns the authoritative journal (see `MessageType.MissionHistory`), so
 * this cap only bounds the in-memory mirror used by the journal UI and
 * the one-shot seed payload uploaded during the localStorage→server
 * migration. Must match the server-side cap in
 * `packages/server/src/persistence/store.ts`.
 */
const COMPLETED_MISSION_HISTORY_CAP = 200;

/**
 * Legacy localStorage key used before the completed-mission journal
 * moved server-side. Kept around solely so we can read it once on
 * login, ship the entries to the server as a one-shot seed, and then
 * delete it. Never written by the live client anymore.
 */
const LEGACY_COMPLETED_MISSIONS_LS_PREFIX = 'vs_completed_missions:';

/**
 * A historical record of a completed mission. We snapshot the title +
 * description (UI keeps them readable in the journal even after the
 * server has expired the underlying template) plus the awarded rewards
 * for at-a-glance "what did this earn me" feedback.
 */
export interface CompletedMissionEntry {
  id: string;
  title: string;
  description: string;
  isDaily?: boolean;
  isWeekly?: boolean;
  rewards: { xp: number; credits: number };
  completedAt: number;
}

function lerpScalar(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function cloneVec3(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z };
}

function cloneTransform(t: ClientTransform): ClientTransform {
  return {
    position: cloneVec3(t.position),
    rotation: cloneVec3(t.rotation),
    velocity: cloneVec3(t.velocity),
  };
}

function zeroVec3(): Vec3 {
  return { x: 0, y: 0, z: 0 };
}

function zeroTransform(): ClientTransform {
  return { position: zeroVec3(), rotation: zeroVec3(), velocity: zeroVec3() };
}

function compactToTransform(c: CompactEntity): ClientTransform {
  // vx/vy/vz are sparse on the wire — the server drops any axis that's
  // exactly zero (most stationary NPCs, planets, parked ships). Treat
  // an absent axis as 0 so the client interpolator keeps the entity
  // still instead of NaN-ing into the void.
  return {
    position: { x: c.px, y: c.py, z: c.pz },
    rotation: { x: c.rx, y: c.ry, z: c.rz },
    velocity: { x: c.vx ?? 0, y: c.vy ?? 0, z: c.vz ?? 0 },
  };
}

export class GameState {
  private entities = new Map<EntityId, ClientEntity>();
  private damageEffectListeners: Array<(e: EntityDamageEffectEvent) => void> = [];
  private localPlayerId: EntityId | null = null;
  /**
   * One-shot flag set by {@link handleSectorChange} that forces the next
   * server reconcile of the local player ship to hard-snap the position
   * (instead of smooth-blending). Without this, a teleport whose
   * orbital → surface delta happens to land below the
   * {@link reconcileLocalPlayer} hard-snap threshold (~100 u) ends up
   * smooth-blending the ship across the warp gap, leaving the camera
   * looking at the *old* position for several frames after the new
   * sector loads — which is the "I see the surface entities but my
   * ship is still in space" symptom users reported on landing.
   */
  private _forceLocalPlayerSnap = false;
  /**
   * When true, {@link applyLocalInput} applies the same downward
   * gravity that the server applies in {@link applySurfaceGravity}.
   * Without it, the client predicts zero gravity while the server
   * predicts {@link SURFACE_GRAVITY}, the two integrate into
   * different Y values every tick, and the reconcile error keeps
   * tugging the ship downward — visually "I'm still flying / why is
   * the camera vibrating". Toggled from main.ts SectorChange handler.
   */
  private _onSurface = false;
  /**
   * Optional client-side terrain sampler set when a surface blueprint
   * is loaded ({@link surface-renderer.SurfaceRenderer.sampleHeightAt}).
   * `applyLocalInput` uses it to clamp the predicted ship to the
   * heightmap so the player rides the terrain instead of half-sinking
   * into hills between server reconciles.
   */
  private _heightSampler: ((x: number, z: number) => number | null) | null = null;
  /**
   * Half-width of the active surface arena (so X/Z range is
   * `[-half, +half]`). Set when the SurfaceData message arrives;
   * cleared on liftoff via {@link setOnSurface}(false). Used by
   * {@link applyLocalInput} to mirror the server's hard X/Z
   * boundary clamp — without this the predictor was running the
   * ship past the rim wall and fighting the server's snap-back
   * each broadcast, producing the "press W into the wall, ship
   * stutters" feel that the user described as a freeze.
   */
  private _surfaceHalfSize = 0;
  /**
   * Pre-baked list of collidable flora positions for the active
   * surface (Tree / Mushroom / IcePillar / Crystal / Boulder
   * cylinders). Walked once per local-prediction tick to push the
   * ship out of overlapping trees so the predictor agrees with
   * the server's flora collision and the player feels a real wall
   * when they fly into a trunk instead of seeing a reconcile snap-
   * back. Empty array when no surface is active.
   */
  private _surfaceFloraColliders: ReadonlyArray<{
    x: number;
    z: number;
    r: number;
    rSq: number;
  }> = [];
  /**
   * Local-only timestamp of the last surface jump impulse the
   * predictor fired. Used to gate the next jump by
   * {@link SURFACE_JUMP_COOLDOWN_MS} so a held UP key produces the
   * same jump-fall-jump rhythm the server enforces. Mirrored as a
   * cheap cooldown rather than read off the entity's `_extra` because
   * the server stamps that field server-side and only re-broadcasts
   * it sparsely — the local predictor needs an immediately-available
   * tick.
   *
   * Initialised to `Number.NEGATIVE_INFINITY` so the very first jump
   * the player ever attempts always passes the cooldown gate — without
   * this, a fresh page-load comparing `performance.now()` (which can
   * be < `SURFACE_JUMP_COOLDOWN_MS` if the user lands quickly) against
   * `0` would silently swallow the impulse and the user would report
   * "jump stopped working". {@link setOnSurface}(true) also resets it
   * so each *new* landing is guaranteed to allow an immediate hop
   * (the old cooldown from the previous planet shouldn't carry over).
   */
  private _lastSurfaceJumpAt = Number.NEGATIVE_INFINITY;

  private playerState: LocalPlayerState = {
    entityId: null,
    accountPlayerId: null,
    name: '',
    hp: 100,
    maxHp: 100,
    shield: 50,
    maxShield: 50,
    energy: 100,
    maxEnergy: 100,
    boostLocked: false,
    damage: 5,
    credits: 0,
    xp: 0,
    xpRequired: xpRequiredForLevel(2),
    level: 1,
    inventory: [],
    sector: { x: 0, y: 0, z: 0 },
    shipClass: null,
    raceId: null,
    isDocked: false,
    ownedShipClasses: [],
    equipment: {
      equippedWeaponId: DEFAULT_WEAPON_ID,
      equippedShieldId: DEFAULT_SHIELD_ID,
      equippedEngineId: DEFAULT_ENGINE_ID,
      weaponAmmo: -1,
      weaponMaxAmmo: -1,
      shieldCharges: -1,
      shieldMaxCharges: -1,
      ownedWeapons: [{ id: DEFAULT_WEAPON_ID, ammo: -1 }],
      ownedShields: [{ id: DEFAULT_SHIELD_ID, charges: -1 }],
      ownedEngines: [{ id: DEFAULT_ENGINE_ID }],
    },
  };

  private notifications: GameNotification[] = [];
  private sectorEntities = new Set<EntityId>();
  private discoveredSectors = new Set<string>();
  /**
   * Player-chosen waypoint sector. Pure client-side navigation hint: the
   * HUD renders an arrow on the radar pointing at this sector's centre
   * and the universe map highlights it. Cleared on logout / pilot swap
   * (via {@link clear}) and when the player manually removes it from
   * the map UI. `null` means "no active waypoint".
   */
  private navigationTarget: SectorCoord | null = null;
  /**
   * Mission id whose `targetSector` is currently displayed on the radar.
   * Optional companion to {@link navigationTarget}: when the pilot
   * toggles "Show in Radar" on an active mission card we pin the
   * mission id here so the mission journal can render the on/off
   * state of the toggle. Cleared automatically whenever
   * {@link setNavigationTarget} is called without an explicit
   * `missionId` (e.g. via the universe map's manual destination
   * picker), and whenever the mission falls off the active log
   * (completed / abandoned / expired). At most one mission can be
   * "in radar" at a time — the toggle is exclusive by construction.
   */
  private radarMissionId: string | null = null;
  /** Authoritative sector ownership + siege state from the server. */
  private sectorClaims = new Map<string, SectorClaim>();
  /**
   * Currently-active sector events (mothership alerts, raids, faction
   * skirmishes …). Keyed by event id so the universe map and warp-gate
   * UIs can paint indicators on affected sectors and describe what's
   * happening when the player selects them. Server pushes a snapshot
   * on auth and incremental updates as events fire / expire.
   */
  private activeSectorEvents = new Map<string, ActiveSectorEvent>();
  /**
   * Latest mission catalogue from the station the pilot docked at (from
   * {@link MessageType.MissionList} with `scope='station'`). Cleared on
   * undock / sector change.
   */
  private stationMissions: WireMission[] = [];
  /**
   * Active mission log for the local pilot. Server-authoritative; updated
   * on `MissionList` (scope='log'), `MissionProgress`, and `MissionComplete`.
   * Capped at 5 by the server tracker, so this stays small.
   */
  private activeMissions: WireMission[] = [];

  /**
   * Local mirror of the pilot's completed-mission journal. The server
   * is the single source of truth — it persists the list under the
   * pilot's account, pushes a snapshot on join, and pushes an updated
   * snapshot after every new completion via
   * {@link MessageType.MissionHistory}.
   *
   * Used to live in localStorage which lost the journal on a device
   * switch / cleared profile / private window — that violated the
   * "MMO state must be authoritative server-side" rule.
   *
   * Capped at {@link COMPLETED_MISSION_HISTORY_CAP} entries (most-recent
   * first) to bound memory; the cap matches the server-side cap.
   */
  private completedMissions: CompletedMissionEntry[] = [];
  private completedMissionsAccountKey: string | null = null;

  /**
   * Server-authoritative friends list + pending request queues. Updated by
   * the corresponding `FriendsListData` / `FriendRequestsData` messages
   * the server pushes on join and after every change.
   */
  private friends: FriendEntry[] = [];
  private incomingFriendRequests: FriendRequestEntry[] = [];
  private outgoingFriendRequests: FriendRequestEntry[] = [];

  /**
   * Latest marketplace listings snapshots. The "all" page is what the
   * marketplace browser renders; "mine" feeds the seller's "My Listings"
   * tab. Both arrive from the server via `MarketplaceListings`.
   */
  private marketplaceAll: MarketplaceListing[] = [];
  private marketplaceMine: MarketplaceListing[] = [];

  /**
   * Latest snapshot of the inter-station bounty board. Server is the
   * sole authority — every new snapshot replaces the cache wholesale.
   * Sorted by reward desc on the wire; the UI just renders in order.
   */
  private bountyContracts: SystemBountyContract[] = [];
  /** Server wall-clock at the moment the last snapshot landed. */
  private bountyServerTime = 0;
  private marketplaceServerTime: number = 0;
  /**
   * External hooks fired when a new entity appears in the local state
   * (spawned either via an explicit EntitySpawn message or implicitly
   * when it first shows up in an EntityUpdate batch). Used by the audio
   * system to play weapon sounds on projectile creation.
   */
  private spawnListeners: Array<(entity: ClientEntity) => void> = [];
  private _lastServerTransform: ClientTransform | null = null;
  private _lastReconcileDist = 0;

  // Residual position error between client-predicted and server-authoritative
  // local-player position. Applied gradually every frame (not in one jump per
  // server packet) so the camera — which now tracks the ship rigidly — does
  // not visibly twitch at each reconciliation tick.
  private _reconcileError: Vec3 = { x: 0, y: 0, z: 0 };

  setLocalPlayerId(id: EntityId): void {
    this.localPlayerId = id;
    this.playerState.entityId = id;
  }

  /**
   * Toggle surface-mode prediction. Called from the SectorChange
   * handler in main.ts so the client predictor mirrors the server's
   * gravity branch the moment the player crosses into a surface
   * sector. Liftoff resets it to `false` and the predictor returns
   * to the standard 6DoF space integration.
   */
  setOnSurface(on: boolean): void {
    this._onSurface = on;
    if (on) {
      // Each new landing must allow an immediate jump regardless of
      // when the player last hopped on a *different* planet — the
      // cooldown from the previous surface session shouldn't carry
      // over and silently swallow the first hop on the new world.
      this._lastSurfaceJumpAt = Number.NEGATIVE_INFINITY;
    } else {
      this._heightSampler = null;
      this._surfaceHalfSize = 0;
      this._surfaceFloraColliders = [];
    }
  }

  /**
   * Tell the predictor the half-width of the active surface so it
   * can mirror the server's X/Z boundary clamp. Pulled from the
   * {@link SurfaceData} message right after the height sampler is
   * wired up. Pass `0` to disable (no clamp applied).
   */
  setSurfaceHalfSize(half: number): void {
    this._surfaceHalfSize = Math.max(0, half);
  }

  /**
   * Inject the pre-computed flora collider list (built via
   * {@link collectFloraColliders} on the shared blueprint) so the
   * client predictor can mirror the server's flora collision and
   * stop the ship from sailing through trees. Pass `[]` to disable.
   */
  setSurfaceFloraColliders(
    colliders: ReadonlyArray<{ x: number; z: number; r: number; rSq: number }>,
  ): void {
    this._surfaceFloraColliders = colliders;
  }

  /**
   * Inject a height-sampling callback (typically backed by the
   * SurfaceRenderer's heightmap) so the predictor can clamp the
   * player ship to the terrain between server snapshots. Pass
   * `null` to clear (handled implicitly by `setOnSurface(false)`).
   */
  setSurfaceHeightSampler(sampler: ((x: number, z: number) => number | null) | null): void {
    this._heightSampler = sampler;
  }

  applyLocalInput(
    input: { thrust: Vec3; rotation: Vec3; isBoosting: boolean },
    dt: number,
  ): void {
    if (!this.localPlayerId) return;
    const entity = this.entities.get(this.localPlayerId);
    if (!entity) return;

    // ── Rotation ordering note (READ CAREFULLY) ─────────────────────
    // The server's `applyInput` does `entity.rotX = input.rotation.x`
    // (assigns input as absolute) and then builds the thrust basis
    // from that. The client's network layer in `client.ts → sendInput`
    // sends `player.transform.rotation` as the absolute rotation —
    // and crucially that send happens in `main.ts` BEFORE this
    // method runs:
    //
    //   1. main.ts: this.network.sendInput(inputState)   // sends R_T
    //   2. main.ts: this.state.applyLocalInput(inputState, dt) // ← we're here
    //
    // So when the server processes tick T, it has R_T, the rotation
    // as of the START of this frame on the client. For predictor /
    // server parity we MUST also build the basis from R_T (the
    // current `entity.transform.rotation`), NOT from R_T + this
    // frame's mouse delta. The trailing `rotation.* += input.rotation.*`
    // at the end of this method is what advances `R_T → R_(T+1)` so
    // the NEXT frame's send picks up the integrated rotation.
    //
    // A previous iteration moved the rotation integration to the top
    // of this method "to match the server" — that made the predictor
    // build its thrust basis from R_(T+1) while the server still saw
    // R_T, adding ~1 frame of yaw drift per tick. On a surface this
    // shows up as exactly the user-reported "ship freezes for a beat
    // then snaps to its expected destination" while turning. Keep
    // the delta application at the BOTTOM of this method.

    // ─────────────────────────────────────────────────────────────────
    // Predictor physics MUST MIRROR `entity-manager.applyInput` on the
    // server. Drift between the two = continuous reconcile error =
    // the "ship freezes then snaps" the user keeps reporting on
    // surfaces (where the ship is always pitched at the terrain, so
    // any pitch-vs-flat asymmetry in the thrust math compounds every
    // tick into a 5–15 u rolling offset that the bleed has to keep
    // clawing back).
    //
    // The previous version of this method:
    //   - Used `baseSpeed = isBoosting ? 400 : 200`, hard-coding a 2×
    //     boost ratio that only matches the basic thruster. Any
    //     equipped engine with a different `boostMultiplier` (most
    //     of the tier table!) drifts immediately.
    //   - Multiplied only by `shipClass.thrustMul`, omitting:
    //         • race traits (`getRaceTraits(raceId).thrustMul`)
    //         • engine acceleration ratio
    //           (`engine.acceleration / ENGINE_BASELINE.acceleration`)
    //         • active thrust buffs (`buffThrustMul` server-side)
    //   - Built `wxThrust / wyThrust / wzThrust` with a SIMPLIFIED 2D
    //     yaw projection (only `cosY/sinY` and `cosX` applied
    //     inconsistently). The server uses the full
    //         fX = -sinY * cosX
    //         fY =  sinX
    //         fZ = -cosY * cosX
    //         rX =  cosY
    //         rZ = -sinY
    //     forward / right basis. The biggest gap: the client never
    //     translated `thrust.z * sinX` into Y motion, so any pitched
    //     forward thrust on the server climbed faster than the
    //     predicted altitude on the client — which is exactly the
    //     "ship freezes mid-stride then snaps forward" beat on a
    //     planet, because the X/Z components were ALSO off by the
    //     `cosX` factor every frame the pilot was looking at the
    //     ground.
    //
    // We can't reach the server's `buffThrustMul` from here (the
    // active-buffs HUD update streams them separately), but a 1.0
    // fallback there only matters while a temporary buff is active
    // — outside that window the predictor still tracks the server.
    const raceTraits = getRaceTraits(this.playerState.raceId ?? undefined);
    const shipTraits = getShipClassTraits(this.playerState.shipClass ?? undefined);
    const equipment = this.playerState.equipment;
    const engineId = equipment?.equippedEngineId ?? DEFAULT_ENGINE_ID;
    const engine = lookupEngine(engineId);
    const accelMul = engine
      ? engine.acceleration / ENGINE_BASELINE.acceleration
      : 1;
    const engineBoostMul = engine?.boostMultiplier ?? 2;
    const boostAvailable = this.playerState.energy > 0 && !this.playerState.boostLocked;
    const effectiveBoost = input.isBoosting && boostAvailable;
    const boostMul = effectiveBoost ? engineBoostMul : 1;
    const thrustMul = raceTraits.thrustMul * shipTraits.thrustMul;
    const speed = BASE_THRUST_FORCE * accelMul * boostMul * thrustMul;

    const sinY = Math.sin(entity.transform.rotation.y);
    const cosY = Math.cos(entity.transform.rotation.y);
    const sinX = Math.sin(entity.transform.rotation.x);
    const cosX = Math.cos(entity.transform.rotation.x);

    // Surface mode strips the elevator term: SPACE / UP no longer
    // produces continuous lift — it's consumed below as a one-shot
    // jump impulse. With the surface forward basis flattened to the
    // horizontal plane (fY = 0 below) the wy contribution from
    // input is identically zero on a planet, so pitching the nose
    // up + holding W can no longer climb the ship into the sky.
    // Jumping is the only path to altitude on the surface.
    const verticalElevator = this._onSurface ? 0 : input.thrust.y;

    // Forward (`fX,fY,fZ`) and right (`rX,rZ`) basis vectors —
    // identical to the server in `entity-manager.applyInput` so the
    // predicted velocity and the authoritative velocity stay phase-
    // locked on a pitched ship (the on-surface common case).
    //
    // On a surface we use the yaw-only basis so forward thrust is
    // strictly horizontal: nose pitch only steers the camera /
    // reticle, never the velocity vector. The full 6DoF basis is
    // restored in orbit where pulling the nose up to climb is the
    // intended way to gain altitude.
    const fX = this._onSurface ? -sinY : -sinY * cosX;
    const fY = this._onSurface ? 0 : sinX;
    const fZ = this._onSurface ? -cosY : -cosY * cosX;
    const rX = cosY;
    const rZ = -sinY;
    const wxThrust = (input.thrust.x * rX + input.thrust.z * fX) * speed;
    const wyThrust = (verticalElevator + input.thrust.z * fY) * speed;
    const wzThrust = (input.thrust.x * rZ + input.thrust.z * fZ) * speed;

    const damping = Math.pow(0.15, dt);
    entity.transform.velocity.x =
      entity.transform.velocity.x * damping + wxThrust * dt;
    entity.transform.velocity.y =
      entity.transform.velocity.y * damping + wyThrust * dt;
    entity.transform.velocity.z =
      entity.transform.velocity.z * damping + wzThrust * dt;

    // Surface jump impulse — mirror the server's stateless cooldown
    // gate so the predicted ship arcs the same way the authoritative
    // one will. The impulse is applied BEFORE position integration
    // (matching `entity-manager.applyInput` on the server) so the
    // first jump frame moves Y by exactly `vy * dt` on both sides.
    if (
      this._onSurface &&
      input.thrust.y > 0 &&
      this._heightSampler
    ) {
      const ground = this._heightSampler(
        entity.transform.position.x,
        entity.transform.position.z,
      );
      if (ground != null) {
        const grounded =
          entity.transform.position.y <=
          ground + SURFACE_HOVER_OFFSET + SURFACE_JUMP_GROUNDED_TOLERANCE;
        const now = performance.now();
        if (grounded && now - this._lastSurfaceJumpAt >= SURFACE_JUMP_COOLDOWN_MS) {
          entity.transform.velocity.y = SURFACE_JUMP_VELOCITY;
          this._lastSurfaceJumpAt = now;
        }
      }
    }

    entity.transform.position.x += entity.transform.velocity.x * dt;
    entity.transform.position.y += entity.transform.velocity.y * dt;
    entity.transform.position.z += entity.transform.velocity.z * dt;

    // ── Mirror server `updatePhysics` max-speed clamp ──────────────
    // The server clamps velocity magnitude AFTER integrating position
    // (entity-manager.ts → `updatePhysics`). Without the same clamp
    // here, every boost beat the predictor accelerates past the cap
    // for one tick before the next server snapshot snaps it back,
    // producing a steady-state 5–15 u rolling reconcile error that
    // the bleed has to keep clawing away. On a surface this is the
    // last meaningful source of "the ship moves forward in fits and
    // starts even though I'm holding W".
    const engineSpeedMul = engine
      ? engine.maxSpeed / ENGINE_BASELINE.maxSpeed
      : 1;
    const effectiveMax = BASE_MAX_SPEED * engineSpeedMul * thrustMul;
    const v = entity.transform.velocity;
    const vMag = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    if (vMag > effectiveMax) {
      const scale = effectiveMax / vMag;
      v.x *= scale;
      v.y *= scale;
      v.z *= scale;
    }

    // Apply gravity AFTER position integration to match the server
    // (`updatePhysics` integrates first, `applySurfaceGravity` then
    // subtracts gravity from vy and ground-clamps Y). When this was
    // applied BEFORE integration the predictor's per-tick Y was
    // `gravity * dt²` lower than the server's, so the altitude
    // reconcile path saw a constant ~0.005 u/frame disagreement
    // and quietly bled the local player back toward the server's
    // Y on every snapshot — the visible symptom was a low-amplitude
    // vertical jitter that the user perceived as "the ship is
    // floating" while flying low over a hill.
    if (this._onSurface) {
      entity.transform.velocity.y -= SURFACE_GRAVITY * dt;
    }

    // ── Flora collision (mirrors server's `applySurfaceGravity`
    // tree push-out). Walks the pre-baked collider list and pushes
    // the ship out of any overlapping tree cylinder, then reflects
    // the inward velocity at 1.4× along the contact normal so the
    // ship bounces off (rather than pinning against the trunk and
    // showing a reconcile flicker on the next server snapshot).
    if (this._onSurface && this._surfaceFloraColliders.length > 0) {
      const px = entity.transform.position;
      const v = entity.transform.velocity;
      const shipR = 2;
      for (const c of this._surfaceFloraColliders) {
        const dx = px.x - c.x;
        const dz = px.z - c.z;
        const minD = c.r + shipR;
        const dSq = dx * dx + dz * dz;
        if (dSq < minD * minD) {
          // Resolve the dead-centre case (`dSq ≈ 0`) by pushing
          // back against the velocity — the side the ship came in
          // from. Without this the ship gets stuck inside the
          // visual trunk on the unlikely tick where it lands
          // exactly on the collider's centre.
          let nx: number; let nz: number;
          if (dSq > 1e-6) {
            const d = Math.sqrt(dSq);
            nx = dx / d;
            nz = dz / d;
          } else {
            const vSq = v.x * v.x + v.z * v.z;
            if (vSq > 1e-6) {
              const vlen = Math.sqrt(vSq);
              nx = -v.x / vlen;
              nz = -v.z / vlen;
            } else {
              nx = 1; nz = 0;
            }
          }
          px.x = c.x + nx * minD;
          px.z = c.z + nz * minD;
          const vRad = v.x * nx + v.z * nz;
          if (vRad < 0) {
            // SLIDE — kill the inward velocity component, keep the
            // tangential one. Mirrors the server's tree response in
            // `applySurfaceGravity` so the predictor and the
            // authoritative simulation agree on what happens at a
            // contact (no more "predictor bounced left, server
            // bounced right, X reconcile error spikes, ship freezes
            // for 200 ms then snaps to the server's final position").
            v.x -= vRad * SURFACE_FLORA_BOUNCE * nx;
            v.z -= vRad * SURFACE_FLORA_BOUNCE * nz;
          }
        }
      }
    }

    // Client-side boundary mirror, in two parts:
    //   1) Hard *circular* wall at the inner edge of the rim
    //      mountain band (`half * 0.85`). The server enforces this
    //      in `checkSectorBoundaries` for surface sectors so the
    //      ship can't climb the visual rim mountains and walk out
    //      onto the plateau beyond. Mirroring it here keeps the
    //      predictor from running past the wall and creating a
    //      reconcile flicker every time the player flies into it.
    //   2) The original X/Z square clamp at ±half as a defensive
    //      fallback (matches server-side fallback behavior).
    if (this._onSurface && this._surfaceHalfSize > 0) {
      const half = this._surfaceHalfSize;
      const wallR = half * 0.85;
      const wallRSq = wallR * wallR;
      const px = entity.transform.position;
      const v = entity.transform.velocity;
      const r2 = px.x * px.x + px.z * px.z;
      if (r2 > wallRSq) {
        const r = Math.sqrt(r2);
        const nx = px.x / r;
        const nz = px.z / r;
        px.x = nx * wallR;
        px.z = nz * wallR;
        const vRad = v.x * nx + v.z * nz;
        if (vRad > 0) {
          v.x -= vRad * SURFACE_WALL_BOUNCE * nx;
          v.z -= vRad * SURFACE_WALL_BOUNCE * nz;
        }
      }
      if (px.x > half) {
        px.x = half;
        if (v.x > 0) v.x = -v.x * 0.2;
      } else if (px.x < -half) {
        px.x = -half;
        if (v.x < 0) v.x = -v.x * 0.2;
      }
      if (px.z > half) {
        px.z = half;
        if (v.z > 0) v.z = -v.z * 0.2;
      } else if (px.z < -half) {
        px.z = -half;
        if (v.z < 0) v.z = -v.z * 0.2;
      }
    }

    // Client-side ground clamp so the ship visibly rides the
    // heightmap between server snapshots instead of dipping into
    // a hill and snapping back when the next reconcile lands.
    // Also mirrors the server's hard jet ceiling so the predictor
    // doesn't fly the ship past where the authoritative simulation
    // would have clamped the climb.
    if (this._onSurface && this._heightSampler) {
      const ground = this._heightSampler(
        entity.transform.position.x,
        entity.transform.position.z,
      );
      if (ground != null) {
        const minY = ground + SURFACE_HOVER_OFFSET;
        if (entity.transform.position.y < minY) {
          entity.transform.position.y = minY;
          if (entity.transform.velocity.y < 0) entity.transform.velocity.y = 0;
        }
        const ceil = ground + SURFACE_JET_CEILING;
        if (entity.transform.position.y > ceil && entity.transform.velocity.y > 0) {
          entity.transform.velocity.y *= Math.max(
            0, 1 - SURFACE_JET_OVERSHOOT_DRAG * dt * 6,
          );
          if (entity.transform.velocity.y > 1) {
            entity.transform.velocity.y = 0;
          }
        }
      }
    }

    // Gradually absorb any pending server-reconciliation error. Using
    // a per-frame exponential decay keeps the correction smooth
    // instead of arriving as a single jolt at each server packet.
    //
    // ── Why "errMag * 3.3" was the freeze ──────────────────────────
    // A previous iteration scaled the bleed rate with the magnitude
    // of the error: `RECONCILE_RATE = max(1.6, errMag * 3.3)`. The
    // intent was "small errors decay slowly, big errors snap fast",
    // but the maths actually fights the player on surfaces:
    //
    //   • At 80 u/s the player advances ≈1.34 u per 60 fps frame.
    //   • A *modest* 10 u error gives RATE = 33 /s, alpha ≈ 0.42.
    //   • The reconcile then drags the predictor back 4.2 u per
    //     frame — three times the forward thrust contribution.
    //
    // The visible result is exactly what the user reported: the
    // ship freezes for a few frames, NPC labels keep moving, and
    // then the predictor "snaps" to the expected position once the
    // error decays. The fix is two-pronged:
    //
    //   1. Use a *gentle* fixed bleed rate on surfaces so the per-
    //      frame correction never dominates intentional motion.
    //   2. Cap the per-frame correction velocity so a single
    //      reconcile component can't outpace the player's input
    //      thrust contribution; if the residual error is genuinely
    //      large, the hard-snap path in `reconcileLocalPlayer` (25 u
    //      threshold) catches it as a one-frame teleport instead.
    //
    // 2.0 /s clears half the error in ~0.35 s — fast enough that the
    // player visibly tracks the server, slow enough that the bleed
    // adds at most 0.2 u per frame at typical error sizes (≪ a
    // single forward step), so flying forward stays smooth.
    const RECONCILE_RATE = this._onSurface ? 2.0 : 4;
    const alpha = 1 - Math.exp(-RECONCILE_RATE * dt);
    let cx = this._reconcileError.x * alpha;
    let cy = this._reconcileError.y * alpha;
    let cz = this._reconcileError.z * alpha;
    if (this._onSurface) {
      // Cap each axis at MAX_RECONCILE_VEL u/s so the bleed can never
      // out-run forward thrust. 12 u/s is ≈15 % of cruise speed —
      // imperceptible as a "drag" but still resolves a steady-state
      // 5 u offset in under a second once the player stops thrusting.
      const MAX_RECONCILE_VEL = 12;
      const maxStep = MAX_RECONCILE_VEL * dt;
      if (cx > maxStep) cx = maxStep; else if (cx < -maxStep) cx = -maxStep;
      if (cy > maxStep) cy = maxStep; else if (cy < -maxStep) cy = -maxStep;
      if (cz > maxStep) cz = maxStep; else if (cz < -maxStep) cz = -maxStep;
    }
    entity.transform.position.x -= cx;
    entity.transform.position.y -= cy;
    entity.transform.position.z -= cz;
    this._reconcileError.x -= cx;
    this._reconcileError.y -= cy;
    this._reconcileError.z -= cz;

    // Integrate the rotation delta AT THE END (see the long ordering
    // comment at the top of this method for why). The pitch clamp
    // mirrors the server's `PITCH_LIMIT = π * 0.35` in
    // `entity-manager.applyInput` so the integrated R_(T+1) the next
    // `sendInput` will hand the server is already pre-clamped — no
    // round-trip surprise where the server clamps and the client
    // reconciles back to the clamp value.
    entity.transform.rotation.x += input.rotation.x;
    entity.transform.rotation.y += input.rotation.y;
    entity.transform.rotation.z += input.rotation.z;

    const PITCH_LIMIT = Math.PI * 0.35;
    entity.transform.rotation.x = Math.max(
      -PITCH_LIMIT,
      Math.min(PITCH_LIMIT, entity.transform.rotation.x),
    );
  }

  interpolateEntities(dt: number): void {
    for (const [id, entity] of this.entities) {
      if (id === this.localPlayerId) continue;

      entity.interpolationAlpha = Math.min(
        1,
        entity.interpolationAlpha + dt * INTERPOLATION_SPEED,
      );
      const t = entity.interpolationAlpha;

      // In-place lerp to avoid allocating three Vec3s per entity per
      // frame. With 40+ surface entities at 60 FPS the previous
      // `lerpVec3` allocator was producing ~7k garbage objects /
      // second, which triggered visible GC pauses while the player
      // moved across the surface — exactly the "freezes when I
      // move" symptom the user reported.
      const prev = entity.previousTransform;
      const tgt = entity.targetTransform;
      const tr = entity.transform;
      tr.position.x = lerpScalar(prev.position.x, tgt.position.x, t);
      tr.position.y = lerpScalar(prev.position.y, tgt.position.y, t);
      tr.position.z = lerpScalar(prev.position.z, tgt.position.z, t);
      tr.rotation.x = lerpScalar(prev.rotation.x, tgt.rotation.x, t);
      tr.rotation.y = lerpScalar(prev.rotation.y, tgt.rotation.y, t);
      tr.rotation.z = lerpScalar(prev.rotation.z, tgt.rotation.z, t);
      tr.velocity.x = lerpScalar(prev.velocity.x, tgt.velocity.x, t);
      tr.velocity.y = lerpScalar(prev.velocity.y, tgt.velocity.y, t);
      tr.velocity.z = lerpScalar(prev.velocity.z, tgt.velocity.z, t);
    }
  }

  processServerUpdate(entities: CompactEntity[]): void {
    for (const compact of entities) {
      const existing = this.entities.get(compact.id);
      if (!existing) {
        this.spawnFromCompact(compact);
        continue;
      }

      const newTarget = compactToTransform(compact);

      if (compact.id === this.localPlayerId) {
        this.reconcileLocalPlayer(existing, newTarget);
        // Hull/shield/energy are authoritative from the server (regen, warp
        // resets, respawn, boost drain, etc.) — sync onto both the entity
        // and the mirrored player state so the HUD reflects live values.
        if (compact.hp !== undefined) {
          existing.hp = compact.hp;
          this.playerState.hp = compact.hp;
        }
        if (compact.sh !== undefined) {
          existing.shield = compact.sh;
          this.playerState.shield = compact.sh;
        }
        if (compact.en !== undefined) {
          this.playerState.energy = compact.en;
        }
        if (compact.men !== undefined) {
          this.playerState.maxEnergy = compact.men;
        }
        // `bl` is sparse on the wire — only sent while the lockout is
        // armed — so an absent/zero field unambiguously means boost is
        // available again.
        this.playerState.boostLocked = compact.bl === 1;
        existing.lastServerUpdate = performance.now();
        continue;
      }

      existing.previousTransform = cloneTransform(existing.transform);
      existing.targetTransform = newTarget;
      existing.interpolationAlpha = 0;
      existing.lastServerUpdate = performance.now();

      if (compact.hp !== undefined) {
        const prevHp = existing.hp;
        if (
          prevHp > compact.hp &&
          (existing.type === EntityType.NPC ||
            existing.type === EntityType.Ship ||
            existing.type === EntityType.Asteroid)
        ) {
          const pos = newTarget.position;
          const evt: EntityDamageEffectEvent = {
            entityId: existing.id,
            entityType: existing.type,
            position: { x: pos.x, y: pos.y, z: pos.z },
            damage: prevHp - compact.hp,
            isKill: compact.hp <= 0,
          };
          for (const fn of this.damageEffectListeners) {
            try {
              fn(evt);
            } catch {
              /* listener must not block state */
            }
          }
        }
        existing.hp = compact.hp;
      }
      if (compact.sh !== undefined) existing.shield = compact.sh;
      if (compact.extra) {
        existing.extra = { ...existing.extra, ...compact.extra };
        // Mirror live `extra.shipClass` updates onto the typed field so
        // a hangar-driven hull swap (server bumps `_extra.shipClass` in
        // `WorldManager.switchShipClass`) is picked up by every consumer
        // that reads `entity.shipClass` directly — most importantly the
        // EntityRenderer, which dispatches `buildShipMesh` on this field
        // and would otherwise keep showing the old hull until the entity
        // despawns. Accepts both the numeric `ShipClass` enum (player
        // ships) and the string slug (NPCs); see the protocol asymmetry
        // noted in `entity-renderer.ts:resolveShipClassSlug`.
        if ('shipClass' in compact.extra) {
          existing.shipClass = compact.extra['shipClass'] as ShipClass | undefined;
        }
      }
    }

    if (this.localPlayerId) {
      const self = this.entities.get(this.localPlayerId);
      const selfCompact = entities.find((e) => e.id === this.localPlayerId);
      if (self && selfCompact?.extra) {
        self.extra = { ...self.extra, ...selfCompact.extra };
        if ('shipClass' in selfCompact.extra) {
          self.shipClass = selfCompact.extra['shipClass'] as ShipClass | undefined;
        }
      }
    }

    this.pruneStaleEntities();
  }

  private _lastPruneTime = 0;

  private pruneStaleEntities(): void {
    const now = performance.now();
    if (now - this._lastPruneTime < 2000) return;
    this._lastPruneTime = now;

    // Comfortably above the server's 2-second keep-alive heartbeat
    // (`KEEPALIVE_TICKS` in packages/server/src/network/connection.ts)
    // so a single missed frame can never silently delete a real
    // entity. The previous 5-second window matched the extra-refresh
    // window exactly and raced with it, causing stationary stations
    // and planets to blink in and out as the two timers drifted past
    // each other.
    const STALE_MS = 10_000;
    for (const [id, entity] of this.entities) {
      if (id === this.localPlayerId) continue;
      if (now - entity.lastServerUpdate > STALE_MS) {
        this.entities.delete(id);
        this.sectorEntities.delete(id);
      }
    }
  }

  private reconcileLocalPlayer(
    entity: ClientEntity,
    serverTransform: ClientTransform,
  ): void {
    // ─────────────────────────────────────────────────────────────
    // Surface-mode Y reconciliation reframes the server's
    // absolute Y as ALTITUDE-OVER-GROUND, then re-anchors that
    // altitude on the *client's* ground sample at the *client's*
    // current (x, z). Without this, the user sees a clear "ship
    // floats up off cliffs against gravity" / "ship freezes for a
    // beat then snaps" pattern, both rooted in the same root cause:
    //
    //   • client predicts forward → samples ground at the freshly-
    //     predicted (x, z) → gets the LOW ground for the new spot.
    //   • server is 50-100 ms behind in (x, z) (network + tick
    //     timing) → samples ground at the OLD (x, z) → gets the
    //     HIGH ground for the previous spot (e.g. the hill the
    //     player is rolling off).
    //   • server clamps Y to `oldGround + HOVER_OFFSET` and
    //     broadcasts that.
    //   • naive reconcile sees a big positive (clientY − serverY) =
    //     "client is too high", and bleeds the position DOWN —
    //     except here it's the OTHER way around: serverY happens
    //     to be HIGHER than clientY because the stale serverGround
    //     is higher than the freshGround the client landed on, so
    //     the reconcile pushes the ship UP at ~100 u/s while
    //     gravity only earns ~9 u/s of downward velocity. The ship
    //     visibly floats against gravity.
    //
    // The fix is: don't compare raw Y. Compare ALTITUDE-OVER-GROUND.
    // We sample ground at server's (x, z) using our local heightmap
    // (client and server share the byte-for-byte blueprint), derive
    // serverAltitude = serverY − serverGroundY, and reconcile to
    // `clientGroundY + serverAltitude` instead. So if both client
    // and server are simply ground-clamped, both altitudes are
    // HOVER_OFFSET and there is NOTHING to reconcile in Y. If the
    // player jumped (server says altitude=20), the client tracks
    // that 20 u arc above its OWN ground at its own (x, z).
    // ─────────────────────────────────────────────────────────────
    let effectiveServerY = serverTransform.position.y;
    if (this._onSurface && this._heightSampler) {
      const serverGround = this._heightSampler(
        serverTransform.position.x,
        serverTransform.position.z,
      );
      const clientGround = this._heightSampler(
        entity.transform.position.x,
        entity.transform.position.z,
      );
      if (serverGround != null && clientGround != null) {
        const serverAltitude = serverTransform.position.y - serverGround;
        effectiveServerY = clientGround + serverAltitude;
      }
    }

    const dx = entity.transform.position.x - serverTransform.position.x;
    const dy = entity.transform.position.y - effectiveServerY;
    const dz = entity.transform.position.z - serverTransform.position.z;
    const distSq = dx * dx + dy * dy + dz * dz;

    this._lastServerTransform = cloneTransform(serverTransform);
    this._lastReconcileDist = Math.sqrt(distSq);

    // Hard-snap on extreme desync (e.g. warp / land / liftoff, or a
    // network hiccup that left the predictor too far ahead of the
    // server). The sector-change handler also pulls this trigger
    // explicitly so a sub-threshold teleport (orbital ↔ surface where
    // the spawn happens to be near the player's previous coords)
    // still snaps cleanly instead of smooth-blending across the warp
    // gap for several frames.
    //
    // Surfaces use a *much* lower threshold (25 u² = 625) than orbital
    // (100 u² = 10 000): on a planet a 30+ u error from a server tick
    // spike used to bleed for a full second under the old slow rate
    // and showed up as the user-reported "ship freezes momentarily
    // then snaps to its expected destination". Snapping here makes the
    // recovery a single-frame teleport — visible as a brief pop, but
    // far less jarring than a sustained freeze. The adaptive bleed
    // rate handles everything below the snap threshold.
    const SNAP_DIST_SQ = this._onSurface ? 625 : 10000;
    if (distSq > SNAP_DIST_SQ || this._forceLocalPlayerSnap) {
      this._forceLocalPlayerSnap = false;
      entity.transform.position = cloneVec3(serverTransform.position);
      entity.transform.velocity = cloneVec3(serverTransform.velocity);
      entity.transform.rotation = cloneVec3(serverTransform.rotation);
      this._reconcileError.x = 0;
      this._reconcileError.y = 0;
      this._reconcileError.z = 0;
      return;
    }

    // Record the residual error; applyLocalInput() will bleed it off
    // smoothly over the next few frames. This avoids the per-packet jump
    // that the camera's rigid tracking would otherwise expose as jitter.
    this._reconcileError.x = dx;
    this._reconcileError.y = dy;
    this._reconcileError.z = dz;

    // Velocity correction can stay instantaneous — it only affects
    // subsequent prediction, not the rendered position this frame.
    const blend = 0.1;
    entity.transform.velocity.x +=
      (serverTransform.velocity.x - entity.transform.velocity.x) * blend;
    entity.transform.velocity.y +=
      (serverTransform.velocity.y - entity.transform.velocity.y) * blend;
    entity.transform.velocity.z +=
      (serverTransform.velocity.z - entity.transform.velocity.z) * blend;

    entity.targetTransform = cloneTransform(serverTransform);
  }

  processSpawn(compact: CompactEntity): void {
    this.spawnFromCompact(compact);
  }

  private spawnFromCompact(compact: CompactEntity): void {
    const transform = compactToTransform(compact);

    const entity: ClientEntity = {
      id: compact.id,
      type: compact.et,
      transform: cloneTransform(transform),
      previousTransform: cloneTransform(transform),
      targetTransform: cloneTransform(transform),
      interpolationAlpha: 1,
      hp: compact.hp ?? 100,
      maxHp: (compact.extra?.['maxHp'] as number) ?? 100,
      shield: compact.sh ?? 0,
      maxShield: (compact.extra?.['maxShield'] as number) ?? 0,
      name: compact.extra?.['name'] as string | undefined,
      shipClass: compact.extra?.['shipClass'] as ShipClass | undefined,
      raceId: compact.extra?.['raceId'] as RaceId | undefined,
      resourceTier: (compact.extra?.['resourceTier'] as ResourceTier | undefined)
        ?? (compact.extra?.['resource'] != null
          ? (parseInt(String(compact.extra['resource']), 10) as ResourceTier)
          : undefined),
      disposition: compact.extra?.['disposition'] as string | undefined,
      npcTemplateKey: compact.extra?.['npcTemplateKey'] as string | undefined,
      ownerId: compact.extra?.['ownerId'] as EntityId | undefined,
      extra: compact.extra ?? {},
      lastServerUpdate: performance.now(),
    };

    this.entities.set(compact.id, entity);
    this.sectorEntities.add(compact.id);

    if (compact.extra?.['isLocalPlayer']) {
      this.setLocalPlayerId(compact.id);
    }

    for (const listener of this.spawnListeners) {
      try {
        listener(entity);
      } catch {
        /* listeners must not block state updates */
      }
    }
  }

  onEntitySpawn(listener: (entity: ClientEntity) => void): () => void {
    this.spawnListeners.push(listener);
    return () => {
      const idx = this.spawnListeners.indexOf(listener);
      if (idx >= 0) this.spawnListeners.splice(idx, 1);
    };
  }

  /**
   * Hull damage on NPCs / ships / asteroids from `EntityUpdate` (authoritative
   * HP), for hit/streak VFX tied to the same updates that drive the HP bar.
   */
  onEntityDamageEffect(
    listener: (e: EntityDamageEffectEvent) => void,
  ): () => void {
    this.damageEffectListeners.push(listener);
    return () => {
      const idx = this.damageEffectListeners.indexOf(listener);
      if (idx >= 0) this.damageEffectListeners.splice(idx, 1);
    };
  }

  processDespawn(entityId: EntityId): void {
    this.entities.delete(entityId);
    this.sectorEntities.delete(entityId);
    if (entityId === this.localPlayerId) {
      this.localPlayerId = null;
      this.playerState.entityId = null;
    }
  }

  setSectorData(data: SectorDataMsg): void {
    this.playerState.sector = data.sector;
    for (const e of data.entities) {
      this.spawnFromCompact(e);
    }
  }

  handleSectorChange(change: SectorChangeMsg): void {
    this.playerState.sector = change.sector;
    // Drop every cached non-local entity. Without this the previous
    // sector's planets / asteroids / NPCs hang around for up to
    // {@link STALE_MS} after the player crosses into a new one,
    // because the new sector's broadcast simply stops mentioning
    // them — `pruneStaleEntities` is the only thing that reaps
    // them, and that runs on a 10 s timer. The symptom is "I
    // landed but the orbital sector is still rendered behind the
    // sky dome"; the same race used to cause the shorter-lived
    // "old planet flickers in for a frame after warp" glitch.
    // The local player ship is preserved so prediction state +
    // camera / inventory references survive the transition.
    for (const [id] of this.entities) {
      if (id === this.localPlayerId) continue;
      this.entities.delete(id);
      this.sectorEntities.delete(id);
    }
    // Make the next server reconcile snap the player straight to the
    // authoritative spawn position, even if the orbital-vs-surface
    // delta happens to be small enough that the regular threshold
    // would smooth-blend instead. See {@link _forceLocalPlayerSnap}.
    this._forceLocalPlayerSnap = true;
  }

  setPlayerProfile(profile: PlayerStateMsg): void {
    this.playerState.name = profile.username ?? this.playerState.name;
    this.playerState.credits = profile.credits ?? this.playerState.credits;
    this.playerState.xp = profile.xp ?? this.playerState.xp;
    this.playerState.level = profile.level ?? this.playerState.level;
    this.playerState.xpRequired = xpRequiredForLevel((profile.level ?? 1) + 1);
    this.playerState.sector = profile.sector ?? this.playerState.sector;
    // Remember race + ship so local input prediction (applyLocalInput) and
    // any UI that reads playerState pick up the correct multipliers. The
    // server's CompactEntity broadcasts for this player will also stamp
    // `shipClass` onto the entity's _extra bag.
    if (profile.race !== undefined) this.playerState.raceId = profile.race;
    if (profile.shipClass !== undefined) this.playerState.shipClass = profile.shipClass;

    const lvStats = playerStatsForLevel(profile.level ?? 1);
    this.playerState.maxHp = lvStats.maxHp;
    this.playerState.maxShield = lvStats.maxShield;
    this.playerState.damage = lvStats.damage;
    this.playerState.hp = lvStats.maxHp;
    this.playerState.shield = lvStats.maxShield;

    if (profile.inventory) {
      this.playerState.inventory = profile.inventory.map((i) => ({
        itemId: i.itemId,
        quantity: i.quantity,
        name: i.itemId,
      }));
    }
    if (profile.playerId) {
      this.setLocalPlayerId(profile.playerId);
    }
    if (profile.accountPlayerId !== undefined) {
      const next = profile.accountPlayerId ?? null;
      const prev = this.playerState.accountPlayerId;
      this.playerState.accountPlayerId = next;
      if (next !== prev) {
        // Pull the completed-mission history scoped to the freshly
        // resolved account id so the journal can render it without a
        // separate hand-off from main.ts.
        this.setCompletedMissionsAccount(next);
      }
    }
  }

  setHangar(msg: ShipHangarUpdateMsg): void {
    this.playerState.ownedShipClasses = msg.ownedShipClasses.slice() as ShipClass[];
    this.playerState.shipClass = msg.activeShipClass as ShipClass;
  }

  setEquipment(msg: EquipmentUpdateMsg): void {
    this.playerState.equipment = {
      equippedWeaponId: msg.equippedWeaponId,
      equippedShieldId: msg.equippedShieldId,
      equippedEngineId: msg.equippedEngineId ?? DEFAULT_ENGINE_ID,
      weaponAmmo: msg.weaponAmmo,
      weaponMaxAmmo: msg.weaponMaxAmmo,
      shieldCharges: msg.shieldCharges,
      shieldMaxCharges: msg.shieldMaxCharges,
      ownedWeapons: msg.ownedWeapons.slice(),
      ownedShields: msg.ownedShields.slice(),
      ownedEngines: (msg.ownedEngines ?? [{ id: DEFAULT_ENGINE_ID }]).slice(),
    };
  }

  getEquipment(): EquipmentState {
    return this.playerState.equipment;
  }

  pushNotification(
    notification: Omit<GameNotification, 'timestamp'>,
  ): void {
    this.notifications.push({
      ...notification,
      timestamp: performance.now(),
    });
    if (this.notifications.length > MAX_NOTIFICATIONS) {
      this.notifications.shift();
    }

    if (notification.type === 'damage') {
      const dmg = notification.data as {
        targetId: EntityId;
        damage: number;
        shieldDamage?: number;
      };
      if (dmg.targetId === this.localPlayerId) {
        this.playerState.hp = Math.max(
          0,
          this.playerState.hp - dmg.damage,
        );
        if (dmg.shieldDamage !== undefined) {
          this.playerState.shield = Math.max(
            0,
            this.playerState.shield - dmg.shieldDamage,
          );
        }
      }
    }

    if (notification.type === 'xp') {
      const xp = notification.data as { amount: number; totalXp: number; level: number };
      this.playerState.xp = xp.totalXp;
      this.playerState.level = xp.level;
      this.playerState.xpRequired = xpRequiredForLevel(xp.level + 1);
    }

    if (notification.type === 'levelUp') {
      const lvl = notification.data as {
        newLevel: number;
        maxHp?: number;
        maxShield?: number;
        damage?: number;
      };
      this.playerState.level = lvl.newLevel;
      this.playerState.xpRequired = xpRequiredForLevel(lvl.newLevel + 1);
      if (lvl.maxHp != null) this.playerState.maxHp = lvl.maxHp;
      if (lvl.maxShield != null) this.playerState.maxShield = lvl.maxShield;
      if (lvl.damage != null) this.playerState.damage = lvl.damage;
    }

    if (notification.type === 'dockConfirm') {
      this.playerState.isDocked = true;
    }

    if (notification.type === 'respawn') {
      // Respawn is authoritative from the server: heal the local mirror to
      // full, undock, and let the next entity update reconcile the position.
      this.playerState.isDocked = false;
      this.playerState.hp = this.playerState.maxHp;
      this.playerState.shield = this.playerState.maxShield;
      const entity = this.localPlayerId
        ? this.entities.get(this.localPlayerId)
        : null;
      if (entity) {
        entity.hp = entity.maxHp;
        entity.shield = entity.maxShield;
      }
    }
  }

  getEntities(): Map<EntityId, ClientEntity> {
    return this.entities;
  }

  getEntity(id: EntityId): ClientEntity | undefined {
    return this.entities.get(id);
  }

  getPlayerEntity(): ClientEntity | null {
    if (!this.localPlayerId) return null;
    return this.entities.get(this.localPlayerId) ?? null;
  }

  getPlayerState(): Readonly<LocalPlayerState> {
    return this.playerState;
  }

  updateInventoryState(
    inventory: InventorySlot[],
    credits: number,
  ): void {
    this.playerState.inventory = inventory;
    this.playerState.credits = credits;
  }

  getLocalPlayerId(): EntityId | null {
    return this.localPlayerId;
  }

  getNotifications(): readonly GameNotification[] {
    return this.notifications;
  }

  drainNotifications(): GameNotification[] {
    return this.notifications.splice(0);
  }

  getLastServerTransform(): ClientTransform | null {
    return this._lastServerTransform;
  }

  getLastReconcileDistance(): number {
    return this._lastReconcileDist;
  }

  clear(): void {
    this.entities.clear();
    this.sectorEntities.clear();
    this.notifications.length = 0;
    this.discoveredSectors.clear();
    this.navigationTarget = null;
    this.radarMissionId = null;
    this.localPlayerId = null;
    this._lastServerTransform = null;
    this._lastReconcileDist = 0;
    this.sectorClaims.clear();
    this.activeSectorEvents.clear();
    this.stationMissions = [];
    this.activeMissions = [];
    this.playerState.accountPlayerId = null;
  }

  setDiscoveredSectors(sectors: SectorCoord[]): void {
    this.discoveredSectors.clear();
    for (const s of sectors) {
      this.discoveredSectors.add(sectorKey(s));
    }
  }

  addDiscoveredSector(sector: SectorCoord): boolean {
    const key = sectorKey(sector);
    if (this.discoveredSectors.has(key)) return false;
    this.discoveredSectors.add(key);
    return true;
  }

  hasDiscoveredSector(sector: SectorCoord): boolean {
    return this.discoveredSectors.has(sectorKey(sector));
  }

  getDiscoveredSectors(): SectorCoord[] {
    return Array.from(this.discoveredSectors).map((k) => {
      const parts = k.split(',').map(Number);
      return { x: parts[0] ?? 0, y: parts[1] ?? 0, z: parts[2] ?? 0 };
    });
  }

  /**
   * Set the player's active waypoint. The optional `missionId` lets
   * callers tell the journal "this destination came from mission X" so
   * its "Show in Radar" toggle can render as the active selection.
   * Omitting `missionId` (the manual-destination path used by the
   * universe map) clears any previous mission pin so the toggle in
   * the journal flips back to the unset state — without that the
   * journal would lie ("Shown in radar") even though the radar is
   * actually pointing at a freshly-typed custom coordinate.
   *
   * Targeting the player's current sector is silently treated as a
   * clear so the radar arrow doesn't spin on top of the ship.
   */
  setNavigationTarget(sector: SectorCoord | null, missionId: string | null = null): void {
    if (!sector) {
      this.navigationTarget = null;
      this.radarMissionId = null;
      return;
    }
    const cur = this.playerState.sector;
    if (sector.x === cur.x && sector.y === cur.y && sector.z === cur.z) {
      // Targeting the sector you're already in is meaningless; treat as
      // a clear so the radar arrow disappears instead of spinning on
      // top of the player.
      this.navigationTarget = null;
      this.radarMissionId = null;
      return;
    }
    this.navigationTarget = { x: sector.x, y: sector.y, z: sector.z };
    this.radarMissionId = missionId;
  }

  getNavigationTarget(): SectorCoord | null {
    if (!this.navigationTarget) return null;
    return { ...this.navigationTarget };
  }

  /**
   * Mission id the journal should show as "active in radar". `null`
   * means no mission is pinned (either the radar is empty or it's
   * pointing at a manual destination from the universe map).
   */
  getRadarMissionId(): string | null {
    return this.radarMissionId;
  }

  applySectorClaimsSnapshot(claims: SectorClaim[]): void {
    this.sectorClaims.clear();
    for (const c of claims) {
      this.sectorClaims.set(c.sectorKey, structuredClone(c));
    }
  }

  applySectorClaimUpdate(claim: SectorClaim): void {
    this.sectorClaims.set(claim.sectorKey, structuredClone(claim));
  }

  applySiegeUpdate(msg: SiegeUpdateMsg): void {
    const existing = this.sectorClaims.get(msg.sectorKey);
    if (!existing) return;
    if (msg.phase === 'idle') {
      existing.siege = null;
    } else {
      existing.siege = {
        phase: msg.phase,
        attackerId: msg.attackerId,
        attackerName: msg.attackerName,
        startedAt: msg.startedAt,
        endsAt: msg.endsAt,
      };
    }
    this.sectorClaims.set(msg.sectorKey, existing);
  }

  getSectorClaim(sectorKeyStr: string): SectorClaim | undefined {
    return this.sectorClaims.get(sectorKeyStr);
  }

  getAllSectorClaims(): SectorClaim[] {
    return Array.from(this.sectorClaims.values()).map((c) => structuredClone(c));
  }

  // ── Active sector events ───────────────────────────────────────────

  /**
   * Replace the active-event cache with a server snapshot. Called once
   * per session (right after auth) so the warp-gate and universe-map
   * indicators paint correctly the first time the player opens them.
   * Defensively filters out entries whose `endsAt` already passed at
   * receive time.
   */
  applyActiveSectorEventsSnapshot(events: ActiveSectorEvent[]): void {
    this.activeSectorEvents.clear();
    const now = Date.now();
    for (const ev of events) {
      if (ev.endsAt <= now) continue;
      this.activeSectorEvents.set(ev.id, structuredClone(ev));
    }
  }

  /**
   * Apply an incremental update — either an addition (`event` set) or
   * a removal (`removedId` set). Both shapes arrive on the same
   * `SectorEventUpdate` message so the codec table stays small. Stale
   * additions (server clock drift / queueing) are dropped here too.
   */
  applyActiveSectorEventUpdate(opts: {
    event?: ActiveSectorEvent;
    removedId?: string;
  }): void {
    if (opts.removedId) this.activeSectorEvents.delete(opts.removedId);
    if (opts.event) {
      const now = Date.now();
      if (opts.event.endsAt > now) {
        this.activeSectorEvents.set(opts.event.id, structuredClone(opts.event));
      }
    }
  }

  /**
   * Best-effort eviction of expired entries against a wall clock.
   * Mirrors the server-side `tick`. The server emits a removal update
   * for every expiry, but a network hiccup or a brand-new client can
   * miss one — having the client sweep on each map open keeps the
   * indicators honest.
   */
  pruneExpiredSectorEvents(now: number = Date.now()): void {
    for (const [id, ev] of this.activeSectorEvents) {
      if (ev.endsAt <= now) this.activeSectorEvents.delete(id);
    }
  }

  getAllActiveSectorEvents(): ActiveSectorEvent[] {
    this.pruneExpiredSectorEvents();
    return Array.from(this.activeSectorEvents.values()).map((e) => structuredClone(e));
  }

  /**
   * Active events that match the supplied sector coord. Multiple events
   * can target the same sector; callers (info panel) typically render
   * each one as its own card. Returns a fresh array so callers can
   * mutate it without disturbing the cache.
   */
  getActiveEventsForSector(sector: SectorCoord): ActiveSectorEvent[] {
    this.pruneExpiredSectorEvents();
    const out: ActiveSectorEvent[] = [];
    for (const ev of this.activeSectorEvents.values()) {
      if (
        ev.sector.x === sector.x &&
        ev.sector.y === sector.y &&
        ev.sector.z === sector.z
      ) {
        out.push(structuredClone(ev));
      }
    }
    return out;
  }

  setStationMissions(missions: WireMission[]): void {
    this.stationMissions = missions.map((m) => ({ ...m }));
  }

  getStationMissions(): WireMission[] {
    return this.stationMissions.map((m) => ({ ...m }));
  }

  clearStationMissions(): void {
    this.stationMissions = [];
  }

  /** Replace the entire active-mission log (used on join + every dock). */
  setActiveMissions(missions: WireMission[]): void {
    this.activeMissions = missions.map((m) => ({
      ...m,
      objectives: m.objectives.map((o) => ({ ...o })),
    }));
    // Drop the radar pin if the mission it pointed at fell off the
    // active log (completed elsewhere, expired offline, etc.). The
    // navigationTarget itself is left alone — the player may still
    // want the coordinate as a manual destination.
    if (
      this.radarMissionId &&
      !this.activeMissions.some((m) => m.id === this.radarMissionId)
    ) {
      this.radarMissionId = null;
    }
  }

  getActiveMissions(): WireMission[] {
    return this.activeMissions.map((m) => ({
      ...m,
      objectives: m.objectives.map((o) => ({ ...o })),
    }));
  }

  /**
   * Snapshot lookup for a single active mission by id. Returns a deep
   * clone so callers can stash it into the completed-missions history
   * without worrying about it later being mutated by progress updates.
   */
  getActiveMission(missionId: string): WireMission | null {
    const m = this.activeMissions.find((mm) => mm.id === missionId);
    if (!m) return null;
    return { ...m, objectives: m.objectives.map((o) => ({ ...o })) };
  }

  /** True when the player has already accepted the given mission id. */
  hasActiveMission(missionId: string): boolean {
    return this.activeMissions.some((m) => m.id === missionId);
  }

  /**
   * Bind the completed-mission journal to the calling account. The
   * server pushes the authoritative snapshot via
   * {@link MessageType.MissionHistory} immediately after auth, so all
   * we do here is reset the in-memory mirror to "empty" until that
   * push lands. Called from main.ts as soon as the join handshake
   * resolves the stable account id.
   */
  setCompletedMissionsAccount(accountKey: string | null): void {
    this.completedMissionsAccountKey = accountKey;
    this.completedMissions = [];
  }

  /**
   * Replace the local mirror with the freshest server-pushed snapshot.
   * Defensive copy + cap so a misbehaving server message can't bloat
   * the in-memory list past what the journal UI is built to render.
   */
  setCompletedMissionHistory(entries: CompletedMissionEntry[]): void {
    this.completedMissions = entries
      .map((e) => ({ ...e }))
      .slice(0, COMPLETED_MISSION_HISTORY_CAP);
  }

  /**
   * Optimistic local insert when the client receives `MissionComplete`
   * from the server. Avoids a one-frame stutter where the journal "Just
   * Completed" tab flashes empty before the authoritative
   * `MissionHistory` push arrives. The next snapshot push reconciles
   * the list to the server view (dedupe by id).
   */
  recordCompletedMission(entry: CompletedMissionEntry): void {
    this.completedMissions = [
      entry,
      ...this.completedMissions.filter((m) => m.id !== entry.id),
    ].slice(0, COMPLETED_MISSION_HISTORY_CAP);
  }

  getCompletedMissions(): CompletedMissionEntry[] {
    return this.completedMissions.map((m) => ({ ...m }));
  }

  clearCompletedMissions(): void {
    this.completedMissions = [];
  }

  /**
   * One-shot reader for the legacy localStorage journal. Returns any
   * entries from the pre-server-history era so the network layer can
   * upload them as a `MissionHistorySeed` and then delete the local
   * key. Returns an empty array (and does not throw) on every common
   * failure mode (no localStorage, no key set, JSON corruption, etc.).
   *
   * The live client never WRITES this key anymore — this is purely a
   * migration aid that goes away after every existing player has logged
   * in once.
   */
  drainLegacyCompletedMissionsLocalStorage(): CompletedMissionEntry[] {
    if (
      !this.completedMissionsAccountKey ||
      typeof localStorage === 'undefined'
    ) {
      return [];
    }
    const key = LEGACY_COMPLETED_MISSIONS_LS_PREFIX + this.completedMissionsAccountKey;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      // Always remove the legacy key after a single read attempt — even
      // on parse failure — so we don't keep retrying forever and the
      // local cache eventually disappears for every existing pilot.
      localStorage.removeItem(key);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(
          (e): e is CompletedMissionEntry =>
            !!e &&
            typeof (e as CompletedMissionEntry).id === 'string' &&
            typeof (e as CompletedMissionEntry).title === 'string' &&
            typeof (e as CompletedMissionEntry).completedAt === 'number',
        )
        .slice(0, COMPLETED_MISSION_HISTORY_CAP);
    } catch {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      return [];
    }
  }

  /**
   * Apply a `MissionProgress` payload: update the matching mission's
   * objectives in-place. No-op when the mission is not in the active
   * log (e.g. it was abandoned in another tab).
   */
  applyMissionProgress(
    missionId: string,
    objectives: WireMission['objectives'],
  ): void {
    const m = this.activeMissions.find((mm) => mm.id === missionId);
    if (!m) return;
    m.objectives = objectives.map((o) => ({ ...o }));
  }

  /** Drop a mission from the active log (completed / expired / abandoned). */
  removeActiveMission(missionId: string): WireMission | null {
    const idx = this.activeMissions.findIndex((m) => m.id === missionId);
    if (idx === -1) return null;
    const [removed] = this.activeMissions.splice(idx, 1);
    if (this.radarMissionId === missionId) {
      // Mission is gone; un-pin so the journal toggle can't keep
      // claiming the radar belongs to it. The navigationTarget itself
      // stays — losing the radar dot the same instant a mission
      // completes would be jarring, and it's still a valid coordinate.
      this.radarMissionId = null;
    }
    return removed;
  }

  getAccountPlayerId(): string | null {
    return this.playerState.accountPlayerId;
  }

  // ── Friends list ────────────────────────────────────────────────────

  setFriendsList(friends: FriendEntry[]): void {
    this.friends = friends.map((f) => ({ ...f }));
  }

  getFriends(): FriendEntry[] {
    return this.friends.map((f) => ({ ...f }));
  }

  applyFriendStatusUpdate(update: {
    friendKey: string;
    friendName: string;
    online: boolean;
    lastSeen: number;
  }): void {
    const existing = this.friends.find((f) => f.key === update.friendKey);
    if (existing) {
      existing.name = update.friendName;
      existing.online = update.online;
      existing.lastSeen = update.lastSeen;
    } else if (update.online) {
      // Race: friend logged in after the snapshot landed but before a
      // re-fetch — splice them in optimistically so the UI shows them.
      this.friends.push({
        key: update.friendKey,
        name: update.friendName,
        online: true,
        lastSeen: update.lastSeen,
      });
    }
  }

  setFriendRequests(incoming: FriendRequestEntry[], outgoing: FriendRequestEntry[]): void {
    this.incomingFriendRequests = incoming.map((r) => ({ ...r }));
    this.outgoingFriendRequests = outgoing.map((r) => ({ ...r }));
  }

  getIncomingFriendRequests(): FriendRequestEntry[] {
    return this.incomingFriendRequests.map((r) => ({ ...r }));
  }

  getOutgoingFriendRequests(): FriendRequestEntry[] {
    return this.outgoingFriendRequests.map((r) => ({ ...r }));
  }

  // ── Marketplace ─────────────────────────────────────────────────────

  setMarketplaceListings(
    scope: 'all' | 'mine',
    listings: MarketplaceListing[],
    serverTime: number,
  ): void {
    if (scope === 'mine') {
      this.marketplaceMine = listings.map((l) => ({ ...l }));
    } else {
      this.marketplaceAll = listings.map((l) => ({ ...l }));
    }
    this.marketplaceServerTime = serverTime;
  }

  getMarketplaceListings(scope: 'all' | 'mine'): MarketplaceListing[] {
    const src = scope === 'mine' ? this.marketplaceMine : this.marketplaceAll;
    return src.map((l) => ({ ...l }));
  }

  getMarketplaceServerTime(): number {
    return this.marketplaceServerTime;
  }

  /**
   * Replace the cached bounty board with a fresh snapshot from the
   * server. Snapshots are pre-sorted by reward desc; UI consumers just
   * render in the order they receive.
   */
  setBountyContracts(contracts: SystemBountyContract[], serverTime: number): void {
    this.bountyContracts = contracts.map((c) => ({ ...c }));
    this.bountyServerTime = serverTime;
  }

  getBountyContracts(): SystemBountyContract[] {
    return this.bountyContracts.map((c) => ({ ...c }));
  }

  /** Server wall-clock at the moment the last snapshot landed. */
  getBountyServerTime(): number {
    return this.bountyServerTime;
  }
}
